import { useMemo } from "react";
import type { MenuProps } from "antd";
import { useEffectiveAssumedRole } from "@/contexts/AssumedRoleContext";
import type { AssumedRole } from "@/contexts/AssumedRoleContext";
import { isVisibleForRole } from "@/utils/rolePermissions";

type AntdMenuItems = NonNullable<MenuProps["items"]>;
type AntdMenuItem = AntdMenuItems[number];

// Filters Ant Design Menu items (flat or parent+children) by the assumed role.
// Accepts any array type and preserves it; casts internally to antd's ItemType
// for safe property access with proper null/key guards.
// Items without a key are kept as-is (e.g. dividers).
function applyRoleFilterToMenuItems(
  items: AntdMenuItems,
  role: AssumedRole | null,
): AntdMenuItems {
  if (role === null) return items;
  return items.reduce((acc: AntdMenuItems, item: AntdMenuItem) => {
    if (
      !item ||
      !("key" in item) ||
      !item.key ||
      isVisibleForRole(String(item.key), role)
    ) {
      if (item && "children" in item && item.children) {
        const filteredChildren = (item.children as AntdMenuItems).filter(
          (child: AntdMenuItem) =>
            !child ||
            !("key" in child) ||
            !child.key ||
            isVisibleForRole(String(child.key), role),
        );
        if (filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren } as AntdMenuItem);
        }
        // drop parent entirely if all children are hidden
      } else {
        acc.push(item);
      }
    }
    return acc;
  }, []);
}

// Filters group-style sidebar menus (site profile / participant profile).
// Each element is { type: "group", key, label, children: AntdMenuItem[] }.
function applyRoleFilterToTabGroups(
  groups: AntdMenuItems,
  role: AssumedRole | null,
): AntdMenuItems {
  if (role === null) return groups;
  return groups
    .map(
      (group: AntdMenuItem): AntdMenuItem =>
        ({
          ...group,
          children: (
            (group && "children" in group
              ? (group.children as AntdMenuItems)
              : undefined) ?? []
          ).filter(
            (item: AntdMenuItem) =>
              !item ||
              !("key" in item) ||
              !item.key ||
              isVisibleForRole(String(item.key), role),
          ),
        }) as AntdMenuItem,
    )
    .filter(
      (group) =>
        group &&
        "children" in group &&
        Array.isArray(group.children) &&
        (group.children as AntdMenuItems).length > 0,
    );
}

export function useRoleFilter() {
  const assumedRole = useEffectiveAssumedRole();

  // Generic wrappers preserve the caller's array element type (T) while
  // routing internally through properly-typed antd-aware implementation.
  const filterMainNavItems = useMemo(
    () =>
      <T>(items: T[]): T[] =>
        applyRoleFilterToMenuItems(
          items as unknown as AntdMenuItems,
          assumedRole,
        ) as unknown as T[],
    [assumedRole],
  );

  const filterTabGroups = useMemo(
    () =>
      <T>(groups: T[]): T[] =>
        applyRoleFilterToTabGroups(
          groups as unknown as AntdMenuItems,
          assumedRole,
        ) as unknown as T[],
    [assumedRole],
  );

  return { filterMainNavItems, filterTabGroups, assumedRole };
}
